import { useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';

export interface UseBlockNavigationOptions {
  /** Whether navigation should be blocked (e.g., edit mode is active) */
  isBlocking: boolean;
  /** Function to check if form has unsaved changes (is dirty) - called at navigation time */
  isDirty: () => boolean;
  /** Called when navigation is attempted while blocking and dirty */
  onBlock: (navigate: () => void) => void;
}

/**
 * Hook to block navigation when conditions are met (e.g., unsaved changes)
 * Only blocks if both isBlocking AND isDirty are true
 */
export function useBlockNavigation({ isBlocking, isDirty, onBlock }: UseBlockNavigationOptions) {
  const router = useRouter();
  const isNavigatingRef = useRef(false);

  const blockAndShowDialog = useCallback(
    (navigateFn: () => void) => {
      if (isNavigatingRef.current || !isBlocking || !isDirty()) return false;

      onBlock(() => {
        isNavigatingRef.current = true;
        navigateFn();
        setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      });
      return true;
    },
    [onBlock, isBlocking, isDirty]
  );

  // Link clicks (internal and external) - attach listener when blocking is enabled, check dirty at click time
  useEffect(() => {
    if (!isBlocking) return;

    const handleClick = (e: MouseEvent) => {
      if (isNavigatingRef.current) return;

      const link = (e.target as HTMLElement).closest('a[href]');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      if (!isBlocking || !isDirty()) return;

      const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
      const target = link.getAttribute('target');

      const navigateFn = () => {
        if (isExternal || target === '_blank') {
          window.open(href, target || '_blank');
        } else {
          router.push(href);
        }
      };

      if (blockAndShowDialog(navigateFn)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isBlocking, router, blockAndShowDialog, isDirty]);
}
